import { Box, CircularProgress, Flex, Text, useToast, useTranslation } from "@familyzone/component-library";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useCommunityDashboardStore } from "../../../storez/CommunityDashboardStore";
import { useCommunityFeatureFlagStore } from "../../../storez/CommunityFeatureFlagStore";
import { GroupSearchSelector } from "../../GroupSearch/GroupSearchSelector";
import PageWithHeader from "../../templates/PageWithHeader";
import { GroupOption } from "../../../types/Groups";
import { ResponseError } from "../../../types/Api";
import { colors } from "../../../theme";
import SignUpsOverTimeChart from "./SignUpsOverTimeChart";
import ParentEngagement from "./ParentEngagement";
import CommunicationsTable from "./CommunicationsTable";
import LinkStatsCard, { ClaimedStudentsLabel, TakeUpLabel } from "./LinkStatsCard";
import { TableColumn } from "../../../types/table";
import { CSVDownloader, CSVGenerator } from "../../../helpers/CSVExport";

function CommunityDashboard(): JSX.Element {
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const title = t("Dashboard");
  const breadcrumbs = [
    { title: t("Parent"), url: "/community", isActive: false },
    { title: t("Dashboard"), isActive: true },
  ];

  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);

  // We use this to only show one error toast when multiple errors occur.
  const hasFetchedDashboard = useRef(false);

  const { fetchTakeUp, fetchSignUps, fetchAllSignUps, fetchCommunications, fetchTopAppsWithIcons, fetchTopArticles, resetTakeUp } =
    useCommunityDashboardStore((state) => ({
      fetchTakeUp: state.fetchTakeUp,
      fetchSignUps: state.fetchSignUps,
      fetchAllSignUps: state.fetchAllSignUps,
      fetchCommunications: state.fetchCommunications,
      fetchTopAppsWithIcons: state.fetchTopAppsWithIcons,
      fetchTopArticles: state.fetchTopArticles,
      resetTakeUp: state.resetTakeUp,
    }));

  const takeUp = useCommunityDashboardStore((state) => state.takeUp);
  const signUps = useCommunityDashboardStore((state) => state.signUps);
  const signUpsDownloadStatus = useCommunityDashboardStore((state) => state.signUpsDownloadStatus);
  const communications = useCommunityDashboardStore((state) => state.communications);
  const topApps = useCommunityDashboardStore((state) => state.topApps);
  const topArticles = useCommunityDashboardStore((state) => state.topArticles);
  const getOrFetchCommunityFlags = useCommunityFeatureFlagStore.getState().getOrFetch;
  const communityFlags = useCommunityFeatureFlagStore((state) => state.flags);

  const showParentTakeUp = !communityFlags?.disableParentTakeUpDashboardSection;
  const showSignUpsOverTime = !communityFlags?.disableSignUpsOverTimeDashboardSection;
  const showCommunications = !communityFlags?.disableCommunicationsDashboardSection;
  const showParentEngagement = !communityFlags?.disableParentEngagementDashboardSection;

  const handleError = useCallback(
    (err: unknown | ResponseError) => {
      errorToast({
        title: t("Please try again"),
        description: err instanceof ResponseError ? t(err.message) : t("Something went wrong"),
        isClosable: true,
      });
    },
    [errorToast, t]
  );

  useEffect(() => {
    if (!communityFlags) {
      void getOrFetchCommunityFlags().catch((err: unknown) => {
        handleError(err);
      });
      return () => resetTakeUp();
    }

    void (async () => {
      // If the selected group changes, we only need to fetch the take up (when that section is enabled).
      const promises: Array<Promise<unknown>> = [];
      if (showParentTakeUp) {
        promises.push(fetchTakeUp({ groupId: selectedGroup?.id }));
      }

      if (!hasFetchedDashboard.current) {
        hasFetchedDashboard.current = true;
        if (showSignUpsOverTime) {
          promises.push(fetchSignUps());
        }
        if (showCommunications) {
          promises.push(fetchCommunications());
        }
        if (showParentEngagement) {
          promises.push(fetchTopAppsWithIcons(), fetchTopArticles());
        }
      }
      const results = await Promise.allSettled(promises);
      const errors = results
        .filter((r): r is PromiseRejectedResult => r.status === "rejected")
        .map((r) => r.reason as ResponseError | unknown);
      if (errors.length > 0) {
        handleError(errors.length === 1 ? errors[0] : new ResponseError("Multiple errors occurred while fetching dashboard data", 500));
      }
    })();

    return () => resetTakeUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, communityFlags, showParentTakeUp, showSignUpsOverTime, showCommunications, showParentEngagement]);

  const calculateIntegerPercent = (numerator: number, denominator: number): number => {
    if (numerator === 0) return 0;
    if (denominator === 0) return 100;
    return Math.min(Math.round((100 * numerator) / denominator), 100);
  };

  const onGroupSelection = useCallback((selected: GroupOption | null): void => setSelectedGroup(selected), [setSelectedGroup]);

  const usersUptakePercent = useMemo(() => {
    return calculateIntegerPercent(takeUp.data.users.claimedCount, takeUp.data.users.linkedCount);
  }, [takeUp.data.users]);

  const onSignUpsDownload = useCallback(
    (columns: TableColumn[], filename: string): void => {
      void (async () => {
        try {
          const allSignUps = await fetchAllSignUps();
          const csv = CSVGenerator(columns, allSignUps.data);
          CSVDownloader(csv, filename);
        } catch (err) {
          handleError(err);
        }
      })();
    },
    [fetchAllSignUps, handleError]
  );

  return (
    <PageWithHeader title={title} breadcrumbs={breadcrumbs}>
      <Flex direction="column">
        {showParentTakeUp && (
          <>
            <Flex pl="sp24" mt="sp24" width="100%">
              <Text fontFamily="heading" fontWeight="medium" fontSize="lg" color="text.title">
                {t("Parent Take Up")}
              </Text>
            </Flex>
            <Box width="288px" mt="sp24" ml="sp24">
              <GroupSearchSelector
                disabled={takeUp.status === "loading"}
                placeholderText={t("Filter by Group")}
                handleChange={onGroupSelection}
              />
            </Box>
            <Flex p="sp24" gap="sp24" width="100%">
              <LinkStatsCard
                flex={1}
                data-testid="community-takeup-card"
                title={t("Take Up Percentage")}
                loading={takeUp.status === "loading"}
                linkTo={"/community/parent-management"}
                linkText={t("View all parents")}
                chart={
                  <CircularProgress
                    value={usersUptakePercent}
                    isIndeterminate={takeUp.status === "loading"}
                    size="200px"
                    thickness="8px"
                    color={colors.brand["500"]}
                    capIsRound
                  >
                    <TakeUpLabel loading={takeUp.status === "loading"} errored={takeUp.status === "error"} percent={usersUptakePercent} />
                  </CircularProgress>
                }
              />
              <LinkStatsCard
                flex={1}
                data-testid="claimed-students-card"
                title={t("Connected Students")}
                loading={takeUp.status === "loading"}
                linkTo={"/community/parent-management"}
                linkText={t("View parent student relationship")}
                chart={
                  <CircularProgress
                    value={usersUptakePercent}
                    isIndeterminate={takeUp.status === "loading"}
                    size="200px"
                    thickness="8px"
                    color="#3E9F78"
                    capIsRound
                  >
                    <ClaimedStudentsLabel
                      loading={takeUp.status === "loading"}
                      errored={takeUp.status === "error"}
                      claimed={takeUp.data.users.claimedCount}
                      linked={takeUp.data.users.linkedCount}
                    />
                  </CircularProgress>
                }
              />
            </Flex>
          </>
        )}
        {showSignUpsOverTime && (
          <Box px="sp24" pb="sp24">
            <SignUpsOverTimeChart
              data={signUps.data}
              isLoading={signUps.status === "loading"}
              isError={signUps.status === "error"}
              isDownloading={signUpsDownloadStatus === "loading"}
              onDownload={onSignUpsDownload}
            />
          </Box>
        )}
        {showCommunications && (
          <Box px="sp24" pb="sp24">
            <CommunicationsTable
              data={communications.data}
              isLoading={communications.status === "loading"}
              isError={communications.status === "error"}
            />
          </Box>
        )}
        {showParentEngagement && (
          <Box px="sp24" pb="sp24">
            <ParentEngagement
              topApps={topApps.data.data}
              isLoadingTopApps={topApps.status === "loading"}
              isErrorTopApps={topApps.status === "error"}
              topArticles={topArticles.data.topArticles}
              isLoadingTopArticles={topArticles.status === "loading"}
              isErrorTopArticles={topArticles.status === "error"}
              safetyHubUrl={topArticles.data.safetyHubUrl}
            />
          </Box>
        )}
      </Flex>
    </PageWithHeader>
  );
}

export default CommunityDashboard;
